import React, {
    RefObject,
    UIEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    IconButton,
    Dialog,
    Typography,
    Stack,
    Button,
    Paper,
    Chip,
    Box,
} from '@mui/material';
import {
    BlockOutlined,
    CloseOutlined,
    CloudDownloadOutlined,
} from '@ant-design/icons';
import { FileMetadata } from '../../api/types/cms';

import { useTranslation } from '../../contexts/TranslationContext';
import { useTheme } from '@mui/material/styles';
import { useImageBuilder } from '../../utils/image';
import { getReadableFileSizeString } from '../../utils/static-files';

import FileVersions, { FileVersionsRef } from '../cms/FileVersions';
import FileUpload from '../qlive/FileUpload';
import BlurryLoadingImage from '../image/BlurryLoadingImage';
import AutomaticPublishingFileChip from '../cms/AutomaticPublishingFileChip';
import FileDateChangeDialog from './FileDateChangeDialog';

type ParallaxRef = {
    connectOnScroll: (event: UIEvent) => void;
    setListener: (callback: (event: UIEvent) => void) => void;
};

type ParallaxImageProps = {
    parallaxRef?: RefObject<ParallaxRef>;
    selectedFile?: FileMetadata | null;
    onPreview?: (file: FileMetadata, editMode: boolean) => void;
};

class Parallax {
    private scrollListener: (event: UIEvent) => void;

    constructor() {
        this.scrollListener = () => {};
    }

    public connectOnScroll(event: UIEvent) {
        this.scrollListener(event);
    }

    public setListener(callback: (event: UIEvent) => void) {
        this.scrollListener = callback;
    }
}

const parallax = new Parallax();

export function ParallaxImage({
    parallaxRef,
    selectedFile,
    onPreview = () => {},
}: ParallaxImageProps) {
    const timeoutRef = useRef<NodeJS.Timeout>();
    const { translate } = useTranslation();

    const { buildImageUrl, buildFileUrl, buildDownloadUrl } = useImageBuilder();

    const [minimized, setMinimized] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({
        width: 0,
        height: 0,
    });

    const onScroll = (event: UIEvent) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            // @ts-ignore
            if (parseInt(`${event.target.scrollTop}`) > 20) {
                setMinimized(true);
            }

            // @ts-ignore
            if (parseInt(`${event.target.scrollTop}`) < 1) {
                setMinimized(false);
            }
        }, 100);
    };

    useEffect(() => {
        parallaxRef?.current?.setListener(onScroll);
    }, [parallaxRef]);

    const calculateImageDimensions = useCallback(
        (small: boolean = false) => {
            let maxWidth = 500;
            if (small) {
                maxWidth = 300;
            }
            const dimensions = {
                width: maxWidth,
                height: small ? 150 : 200,
            };
            const ratio = selectedFile?.additional_data?.ratio?.split(':');
            if (ratio && ratio[0] && ratio[1]) {
                dimensions.height =
                    (maxWidth / parseInt(ratio[0])) * parseInt(ratio[1]);

                if (
                    selectedFile?.additional_data?.ratio === '1:1' ||
                    dimensions.height > 300
                ) {
                    return {
                        width: maxWidth / 2,
                        height: maxWidth / 2,
                    };
                }
            }
            return dimensions;
        },
        [selectedFile],
    );

    useEffect(() => {
        setImageDimensions(calculateImageDimensions(minimized));
    }, [minimized, calculateImageDimensions]);

    return useMemo(() => {
        if (!selectedFile) {
            return null;
        }

        const isImage = Boolean(selectedFile.mime_type?.includes('image'));
        const isPdf = Boolean(selectedFile.mime_type?.includes('pdf'));

        return (
            <Paper sx={{ borderRadius: 0, p: 2, width: '100%' }}>
                <Stack spacing={1}>
                    <Typography variant="subtitle1">
                        {translate('pages.cms.files.current_active')}
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={2}>
                            {isImage && (
                                <BlurryLoadingImage
                                    preview={buildImageUrl(selectedFile, 50)}
                                    image={buildImageUrl(selectedFile)}
                                    imageStyleClass="fileVersionImage"
                                    onClick={() => {
                                        onPreview(selectedFile, false);
                                    }}
                                    style={{
                                        ...imageDimensions,
                                        transition: `height 100ms, width 100ms`,
                                    }}
                                />
                            )}
                            <Stack spacing={0}>
                                <Typography variant="caption" component="p">
                                    {`${translate('pages.cms.files.version')}: ${selectedFile.version}`}
                                </Typography>
                                <Typography variant="subtitle1" component="p">
                                    {selectedFile.original_name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="p">
                                    {getReadableFileSizeString(
                                        selectedFile.size ?? 0,
                                    )}
                                </Typography>
                                <Box sx={{ py: 1 }}>
                                    <AutomaticPublishingFileChip
                                        version={selectedFile}
                                        variant="overview"
                                    />
                                </Box>
                                <Stack direction="row" alignItems="center">
                                    {(isImage || isPdf) && (
                                        <IconButton
                                            title={translate(
                                                'pages.cms.files.btn.new_tab',
                                            )}
                                            onClick={() =>
                                                window.open(
                                                    isImage
                                                        ? buildImageUrl(
                                                              selectedFile,
                                                          )
                                                        : buildFileUrl(
                                                              selectedFile,
                                                          ),
                                                    '_blank',
                                                )
                                            }
                                            color="warning">
                                            <BlockOutlined />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        title={translate(
                                            'pages.cms.files.btn.download',
                                        )}
                                        onClick={() =>
                                            window.open(
                                                buildDownloadUrl(selectedFile),
                                                '_blank',
                                            )
                                        }
                                        color="success">
                                        <CloudDownloadOutlined />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack
                            justifySelf="flex-end"
                            spacing={1}
                            alignItems="center">
                            <Typography variant="overline" fontWeight="bold">
                                {selectedFile.content_name}
                            </Typography>
                            <Chip label={selectedFile.mime_type} />
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        );
    }, [imageDimensions]);
}

type FileVersionsDialogProps = {
    selectedFile?: FileMetadata | null;
    onClose?: (reload: boolean) => void;
    onPreview?: (file: FileMetadata, openInEditMode: boolean) => void;
};

export default function FileVersionsDialog({
    selectedFile,
    onClose = (reload: boolean = false) => {},
    onPreview = () => {},
}: FileVersionsDialogProps) {
    const theme = useTheme();
    const { translate } = useTranslation();

    const parallaxRef = useRef<ParallaxRef>(parallax);
    const fileVersionsRef = useRef<FileVersionsRef>();

    const [file, setFile] = useState<FileMetadata | null>();
    const [uploadOpen, setUploadOpen] = useState(false);
    const [dateChangeSelection, setDateChangeSelection] =
        useState<FileMetadata>();

    /**
     * Set active file null on file update
     */
    useEffect(() => {
        setFile(null);
    }, [selectedFile]);

    const openUpload = () => setUploadOpen(true);

    const closeUpload = () => setUploadOpen(false);

    const onDateDialogClose = (reload: boolean) => {
        setDateChangeSelection(undefined);
        fileVersionsRef.current?.loadData(reload);
    };

    return (
        <Dialog
            disableEnforceFocus
            key={'file-version-dialog'}
            open={Boolean(selectedFile)}
            onClose={() => onClose(false)}
            maxWidth={'md'}
            scroll="paper"
            disablePortal
            disableScrollLock
            PaperProps={{
                sx: {
                    position: 'relative',
                    overflowY: 'scroll',
                },
                variant: 'elevation',
                component: 'div',
            }}
            onScrollCapture={event =>
                parallaxRef.current?.connectOnScroll(event)
            }
            fullWidth>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    background: theme.palette.grey.A200,
                }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ pl: 2, pr: 1, py: 1 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flex={1}>
                        <Typography variant="h5">{`${translate('pages.cms.files.versions_title')} "${selectedFile?.file_id ?? ''}"`}</Typography>
                        <Button onClick={openUpload}>
                            {translate('pages.cms.files.new_version')}
                        </Button>
                    </Stack>
                    <IconButton
                        aria-label="close"
                        onClick={() => onClose(false)}>
                        <CloseOutlined />
                    </IconButton>
                </Stack>
                <ParallaxImage
                    parallaxRef={parallaxRef}
                    onPreview={onPreview}
                    selectedFile={file}
                />
            </div>
            <FileVersions
                file={selectedFile}
                onPreview={onPreview}
                onClose={onClose}
                onSelectionUpdate={setFile}
                onChangeDates={setDateChangeSelection}
                classRef={fileVersionsRef}
            />
            <FileUpload
                fieldConfig={{
                    type: 'file',
                    name: 'upload',
                    required: false,
                    label: 'upload',
                    value: selectedFile,
                }}
                directOpen={uploadOpen}
                hideButton
                hideFiles
                uploadToPool={selectedFile?.file_pool?.id ?? undefined}
                onClose={closeUpload}
                closeAfterUpload
                onRefresh={() => {
                    fileVersionsRef.current?.loadData(true);
                }}
            />
            <FileDateChangeDialog
                file={dateChangeSelection}
                onClose={onDateDialogClose}
            />
        </Dialog>
    );
}
